import { FC } from 'react';

import { Box } from '@mui/material';

import { trackToggleButtonInputUsageAnalyticsEvent } from 'analytics/events/input-usage';

import { useFormContext, useWatch } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import { timeOptions } from 'utils/DateUtils';

import { DrugSpecificProtocol } from 'models/DrugSpecific';

import { DrugSpecificProtocolForm } from 'views/Modals/ScheduledProtocolModal';
import { DrugSpecificProtocolActivationFields } from 'views/Widgets/DrugSpecificProtocolActivationFields';
import { defaultDrugSpecificIntervalOptions } from 'views/Widgets/DrugSpecificProtocolFields.constants';

import MessageDivider from 'views/Widgets/ProtocolTimesMessageDivider';
import RFHWeekdaySelector from 'views/Widgets/RFHWeekdaySelector';
import { RHFStyledInput } from 'views/Widgets/StyledInput';

import { FormAutocomplete } from 'components/UIkit/atoms/Dropdown';

export const RecurringDrugSpecificFields: FC = () => {
  const { control, register, formState, getValues } = useFormContext<DrugSpecificProtocolForm>();
  const { constantsStore } = useStores();

  const [drug, type, selectedFrequency] = useWatch({
    control,
    name: ['drug', 'type', 'selectedFrequency']
  });
  const currentDrugHourlyTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.Hourly
  );

  const isHourlyType = type?.value === currentDrugHourlyTypeId;

  const currentDrugWeeklyCrsTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.WeeklyCRS
  );

  const currentDrugWeeklyInfectionTypeId = constantsStore.getSpecificDrugTypeId(
    drug?.value,
    DrugSpecificProtocol.WeeklyInfection
  );

  const isWeekDaysFieldVisible =
    type?.value === currentDrugWeeklyCrsTypeId || type?.value === currentDrugWeeklyInfectionTypeId;

  return (
    <>
      <Box display="flex" flexDirection="column" width="fit-content">
        <Box display="flex" alignItems="center" gap="1.2rem" py={20}>
          <span>Report every</span>

          <Box width="60px">
            <RHFStyledInput
              type="number"
              min={1}
              rounded
              isRequired
              disabled
              error={Boolean(formState.errors.frequencyMultiplier)}
              name="frequencyMultiplier"
              register={register}
            />
          </Box>

          <Box width="130px">
            <FormAutocomplete
              sortAlphabetically={false}
              isClearable={false}
              isDisabled
              options={defaultDrugSpecificIntervalOptions}
              name="selectedFrequency"
              isRequired
            />
          </Box>

          {!isHourlyType && (
            <>
              <div>at</div>

              <Box width="130px">
                <FormAutocomplete
                  sortAlphabetically={false}
                  isClearable={false}
                  options={timeOptions}
                  name="selectedStartHour"
                />
              </Box>
            </>
          )}
        </Box>

        {isWeekDaysFieldVisible && (
          <Box py={20}>
            <RFHWeekdaySelector
              control={control}
              error={Boolean(formState.errors.weekdays)}
              name="weekdays"
              forceSingleDaySelection
              onChange={() =>
                trackToggleButtonInputUsageAnalyticsEvent(
                  getValues('weekdays')?.toString(),
                  'Week Days',
                  {
                    virtual_page: 'report protocol modal'
                  }
                )
              }
            />
          </Box>
        )}
      </Box>

      <MessageDivider intervalFrequency={selectedFrequency?.value} />
      <DrugSpecificProtocolActivationFields />
    </>
  );
};
